/**
 * Navigation component with mobile menu
 */

import { useState, useEffect } from 'react';
import { Logo } from '../common/Logo';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useLocation } from 'react-router-dom';

interface NavigationProps {
  sections?: Array<{ id: string; label: string }>;
}

export function Navigation({ sections = [] }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const { isScrolled } = useScrollPosition();
  const location = useLocation();

  // Check if we're on the privacy policy page
  const isPrivacyPage = location.pathname === '/privacy';

  // For privacy page: hide/show navbar on scroll
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Handle scroll direction for privacy page
  useEffect(() => {
    if (!isPrivacyPage) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show navbar when scrolling up, hide when scrolling down
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isPrivacyPage, lastScrollY]);

  // Simplified menu items for privacy page
  const privacyMenu = [{ id: 'home', label: 'Início' }];
  const menuItems = isPrivacyPage ? privacyMenu : sections;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isPrivacyPage) return; // Skip scroll tracking on privacy page

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, isPrivacyPage]);

  const scrollToSection = (sectionId: string) => {
    // If on privacy page and going to home, navigate to home
    if (isPrivacyPage && sectionId === 'home') {
      window.location.href = '/';
      return;
    }

    // If going to contact from privacy page, store in localStorage and navigate
    if (isPrivacyPage && sectionId === 'contact') {
      localStorage.setItem('scrollToSection', 'contact');
      window.location.href = '/';
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 50,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease-out',
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 1rem',
      transform: isPrivacyPage ? (isVisible ? 'translateY(0)' : 'translateY(-100%)') : 'translateY(0)',
      ...(isScrolled ? { boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' } : {})
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        {/* Logo - Always visible on privacy page, otherwise follows original behavior */}
        <button
          onClick={() => isPrivacyPage ? (window.location.href = '/') : scrollToSection('home')}
          className={`navbar-logo-btn navbar-logo-btn-mobile ${isScrolled ? 'scrolled' : ''}`}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            opacity: isPrivacyPage ? 1 : (isMobile ? 1 : (isScrolled ? 1 : 0)),
            transform: isMobile ? 'translateX(-50%)' : (isScrolled || isPrivacyPage ? 'translateX(0)' : 'translateX(-20px)'),
            transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
            pointerEvents: 'auto',
            position: 'absolute',
            left: isMobile ? '50%' : 0
          }}
          aria-label="Ir para início"
        >
          <Logo variant="navbar" />
        </button>

        {/* Desktop Menu */}
        <div className="desktop-nav-menu" style={{
          display: isPrivacyPage ? 'flex' : 'none',
          alignItems: 'center',
          gap: '2rem',
          transition: 'transform 0.5s ease-out',
          transform: isScrolled && !isPrivacyPage ? 'translateX(150px)' : 'translateX(0)'
        }}>
          {menuItems.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="nav-link-custom"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: activeSection === section.id ? 'var(--color-lime-500)' : 'var(--color-gray-700)',
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                position: 'relative',
                transition: 'color 0.3s ease-out'
              }}
            >
              {section.label}
            </button>
          ))}
          <button
            onClick={() => scrollToSection('contact')}
            className="button btn-magreen"
          >
            Orçamento
          </button>
        </div>

        {/* Mobile Menu Button - Always on right */}
        <button
          className="mobile-nav-burger"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={isMobileMenuOpen}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '5px',
            background: 'none',
            border: 'none',
            padding: '0.5rem',
            cursor: 'pointer',
            width: '44px',
            height: '44px',
            alignItems: 'center',
            position: 'absolute',
            right: 0
          }}
        >
          <span style={{
            display: 'block',
            width: '22px',
            height: '2px',
            backgroundColor: 'var(--color-gray-700)',
            transition: 'all 0.3s ease',
            ...(isMobileMenuOpen ? { transform: 'rotate(45deg) translate(5px, 5px)' } : {})
          }}></span>
          <span style={{
            display: 'block',
            width: '22px',
            height: '2px',
            backgroundColor: 'var(--color-gray-700)',
            transition: 'all 0.3s ease',
            opacity: isMobileMenuOpen ? 0 : 1
          }}></span>
          <span style={{
            display: 'block',
            width: '22px',
            height: '2px',
            backgroundColor: 'var(--color-gray-700)',
            transition: 'all 0.3s ease',
            ...(isMobileMenuOpen ? { transform: 'rotate(-45deg) translate(5px, -5px)' } : {})
          }}></span>
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMobileMenuOpen && (
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '80px',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          <div style={{
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
            {menuItems.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                style={{
                  textAlign: 'center',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 500,
                  background: activeSection === section.id ? 'var(--color-lime-50)' : 'transparent',
                  color: activeSection === section.id ? 'var(--color-lime-600)' : 'var(--color-gray-700)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                {section.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contact')}
              className="button btn-magreen"
              style={{ marginTop: '0.5rem', width: '100%' }}
            >
              Orçamento
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (min-width: 1024px) {
          .desktop-nav-menu {
            display: flex !important;
          }
          .mobile-nav-burger {
            display: none !important;
          }
        }

        @media (max-width: 1023px) {
          .desktop-nav-menu {
            display: none !important;
          }
          .mobile-nav-burger {
            display: flex !important;
          }
        }
      `}</style>
    </nav>
  );
}
